import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Users, 
  Calendar, 
  Ticket, 
  TrendingUp, 
  DollarSign,
  Activity,
  BarChart3,
  PieChart
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Logo from '@/components/Logo';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [planUsage, setPlanUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user.role !== 'super_admin') {
      navigate('/dashboard');
      return;
    }
    fetchDashboardData();
  }, [user, navigate]);

  const fetchDashboardData = async () => {
    try {
      const [metricsRes, activityRes, planUsageRes] = await Promise.all([
        axios.get(`${API_URL}/admin/dashboard/metrics`),
        axios.get(`${API_URL}/admin/dashboard/recent-activity?limit=20`),
        axios.get(`${API_URL}/admin/dashboard/plan-usage`)
      ]);

      setMetrics(metricsRes.data);
      setRecentActivity(activityRes.data);
      setPlanUsage(planUsageRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Logo size="xl" linkTo={null} className="mx-auto mb-4" />
          <p className="text-gray-600">Caricamento dashboard...</p>
        </div>
      </div>
    );
  }

  if (!metrics) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div data-testid="admin-dashboard-header" className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex min-w-0 items-center gap-4">
              <Logo size="md" linkTo="/dashboard" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
                <p className="text-sm text-gray-600">Panoramica piattaforma ZonePass</p>
              </div>
            </div>
            <button
              data-testid="admin-dashboard-report-link"
              onClick={() => navigate('/admin?tab=report')}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Report &amp; Note
            </button>
            <button
              data-testid="admin-dashboard-return-link"
              onClick={() => navigate('/dashboard')}
              className="text-purple-600 hover:text-purple-700 font-medium"
            >
              Vai alla Dashboard →
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Tenants</CardTitle>
              <Users className="w-4 h-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.overview.total_tenants}</div>
              <p className="text-xs text-green-600 mt-1">
                {metrics.overview.active_tenants} attivi
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Eventi</CardTitle>
              <Calendar className="w-4 h-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.overview.total_events}</div>
              <p className="text-xs text-green-600 mt-1">
                +{metrics.growth.new_events_7d} questa settimana
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Biglietti</CardTitle>
              <Ticket className="w-4 h-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.overview.total_tickets}</div>
              <p className="text-xs text-gray-600 mt-1">
                {metrics.overview.total_checkins} check-in totali
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Revenue</CardTitle>
              <DollarSign className="w-4 h-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">€{metrics.overview.total_revenue.toFixed(2)}</div>
              <p className="text-xs text-gray-600 mt-1">
                Revenue totale
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Event Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PieChart className="w-5 h-5" />
                Distribuzione Piani
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(metrics.plan_distribution).map(([plan, count]) => (
                  <div key={plan} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${
                        plan === 'basic' ? 'bg-gray-400' :
                        plan === 'pro' ? 'bg-blue-500' :
                        plan === 'full' ? 'bg-purple-600' :
                        'bg-yellow-500'
                      }`}></div>
                      <span className="text-sm font-medium capitalize">{plan}</span>
                    </div>
                    <span className="text-sm font-bold">{count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Eventi per Tipo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">In Persona</span>
                  <span className="text-sm font-bold">{metrics.events.by_type.in_person}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Hybrid</span>
                  <span className="text-sm font-bold">{metrics.events.by_type.hybrid}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Online</span>
                  <span className="text-sm font-bold">{metrics.events.by_type.online}</span>
                </div>
                <div className="pt-3 border-t">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Pubblici</span>
                    <span className="text-sm font-bold text-green-600">{metrics.events.public}</span>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-sm font-medium">Privati</span>
                    <span className="text-sm font-bold text-gray-600">{metrics.events.private}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Top Tenants */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Top 10 Tenant per Eventi
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b">
                  <tr>
                    <th className="text-left py-2 px-4 text-sm font-medium text-gray-600">Tenant</th>
                    <th className="text-left py-2 px-4 text-sm font-medium text-gray-600">Piano</th>
                    <th className="text-right py-2 px-4 text-sm font-medium text-gray-600">Eventi</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.top_tenants.map((tenant, idx) => (
                    <tr key={tenant.tenant_id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm">{tenant.tenant_name}</td>
                      <td className="py-3 px-4 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          tenant.subscription_plan === 'basic' ? 'bg-gray-100 text-gray-700' :
                          tenant.subscription_plan === 'pro' ? 'bg-blue-100 text-blue-700' :
                          tenant.subscription_plan === 'full' ? 'bg-purple-100 text-purple-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {tenant.subscription_plan}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-right font-bold">{tenant.event_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Attività Recente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentActivity.slice(0, 10).map((activity, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex-1">
                    {activity.activity_type === 'event_created' ? (
                      <div>
                        <p className="text-sm font-medium">{activity.title}</p>
                        <p className="text-xs text-gray-600">{activity.tenant_name} - {activity.event_type}</p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-sm font-medium">Nuovo tenant: {activity.name}</p>
                        <p className="text-xs text-gray-600">Piano: {activity.subscription_plan}</p>
                      </div>
                    )}
                  </div>
                  <span className="text-xs text-gray-500">
                    {new Date(activity.created_at).toLocaleDateString('it-IT')}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
